import * as React from "react";
import { FlaskConical, GitCompareArrows, ListChecks, Send, SlidersHorizontal } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { TextInput, EmailInput, PasswordInput } from "../../../ui/components/Form";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  compareSmartApiEndpoint,
  createSmartApiTestSession,
  fetchSmartApiCatalog,
  runSmartApiFlowTest,
  type ShapeDifference,
  type SmartApiCatalog,
  type SmartApiCatalogEndpoint,
  type SmartApiCompareResult,
  type SmartApiFlowTestResult,
  type SmartApiSideResult,
} from "../../lib/api/smart-api-compare";
import * as s from "./SmartApiComparePage.css";

const SECTION_REQUEST = "system-smart-api-request";
const SECTION_RESULTS = "system-smart-api-results";
const SECTION_DIFF = "system-smart-api-diff";
const SECTION_FLOW = "system-smart-api-flow-test";

type Identity = "self" | "test";

interface TestSession {
  email: string;
  smartToken: string;
  proToken: string;
}

function queryToText(query?: Record<string, string>): string {
  return Object.entries(query ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
}

export function parseQueryText(text: string): Record<string, string> {
  const query: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const index = trimmed.indexOf("=");
    const key = (index === -1 ? trimmed : trimmed.slice(0, index)).trim();
    if (key) query[key] = index === -1 ? "" : trimmed.slice(index + 1).trim();
  }
  return query;
}

function bodyToText(endpoint: SmartApiCatalogEndpoint): string {
  if (endpoint.method === "GET") return "";
  return JSON.stringify(endpoint.sampleBody ?? {}, null, 2);
}

function groupEndpoints(endpoints: SmartApiCatalogEndpoint[]): Array<[string, SmartApiCatalogEndpoint[]]> {
  const groups = new Map<string, SmartApiCatalogEndpoint[]>();
  for (const endpoint of endpoints) {
    const list = groups.get(endpoint.group) ?? [];
    list.push(endpoint);
    groups.set(endpoint.group, list);
  }
  return [...groups];
}

function formatBody(side: SmartApiSideResult, emptyLabel: string): string {
  if (side.isJson) return JSON.stringify(side.body, null, 2);
  if (side.bodyText) return side.bodyText;
  return emptyLabel;
}

function SideColumn({ title, side }: { title: string; side: SmartApiSideResult }) {
  const { t } = useI18n();
  const ok = side.status !== null && side.status < 400;
  return (
    <div className={s.column}>
      <div className={s.columnHeader}>
        <span className={s.columnTitle}>{title}</span>
        <div className={s.columnMeta}>
          <span className={ok ? s.pill.ok : s.pill.bad}>
            {side.status === null ? t("pro.system.smartApi.noStatus") : t("pro.system.smartApi.status", { status: side.status })}
          </span>
          <span>{t("pro.system.smartApi.duration", { ms: side.durationMs })}</span>
        </div>
      </div>
      <p className={s.url}>{side.url}</p>
      {side.error ? <Callout variant="error">{side.error}</Callout> : null}
      {side.truncated ? <Callout variant="info">{t("pro.system.smartApi.truncated")}</Callout> : null}
      <pre className={s.codeBlock}>{formatBody(side, t("pro.system.smartApi.emptyBody"))}</pre>
      {Object.keys(side.headers).length > 0 ? (
        <div className={s.field}>
          <span className={s.label}>{t("pro.system.smartApi.headers")}</span>
          <dl className={s.headerList}>
            {Object.entries(side.headers).map(([name, value]) => (
              <React.Fragment key={name}>
                <dt className={s.headerName}>{name}</dt>
                <dd className={s.headerValue}>{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

function ShapeDiffTable({ differences }: { differences: ShapeDifference[] }) {
  const { t } = useI18n();
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th className={s.th}>{t("pro.system.smartApi.diff.path")}</th>
            <th className={s.th}>{t("pro.system.smartApi.diff.kind")}</th>
            <th className={s.th}>{t("pro.system.smartApi.diff.smart")}</th>
            <th className={s.th}>{t("pro.system.smartApi.diff.pro")}</th>
          </tr>
        </thead>
        <tbody>
          {differences.map((d) => (
            <tr key={`${d.kind}:${d.path}`}>
              <td className={s.tdMono}>{d.path}</td>
              <td className={s.td}>{t(`pro.system.smartApi.diff.${d.kind}`)}</td>
              <td className={s.tdMono}>{d.smart?.join(" | ") ?? "—"}</td>
              <td className={s.tdMono}>{d.pro?.join(" | ") ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlowTestPanel({ token }: { token: string | null }) {
  const { t } = useI18n();
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<SmartApiFlowTestResult | null>(null);

  const run = async () => {
    if (!token) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await runSmartApiFlowTest(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pro.system.smartApi.flowTest.failed"));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className={s.sectionInner}>
      <Text level="body-small" color="muted">
        {t("pro.system.smartApi.flowTest.intro")}
      </Text>
      <div className={s.actionsRow}>
        <Button color="primary" onClick={() => void run()} disabled={!token || running}>
          <FlaskConical size={14} />
          {t("pro.system.smartApi.flowTest.run")}
        </Button>
        {result ? (
          <span className={result.ok ? s.pill.ok : s.pill.bad}>
            {result.ok ? t("pro.system.smartApi.flowTest.passed") : t("pro.system.smartApi.flowTest.notPassed")}
          </span>
        ) : null}
        {result ? (
          <span className={s.endpointPath}>{t("pro.system.smartApi.flowTest.testUser", { email: result.email })}</span>
        ) : null}
      </div>
      {error ? <Callout variant="error">{error}</Callout> : null}
      {running ? (
        <AuthTransitionScreen variant="compact" message={t("pro.system.smartApi.flowTest.running")} />
      ) : null}
      {result ? (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t("pro.system.smartApi.flowTest.col.step")}</th>
                <th className={s.th}>{t("pro.system.smartApi.flowTest.col.result")}</th>
                <th className={s.th}>{t("pro.system.smartApi.flowTest.col.status")}</th>
                <th className={s.th}>{t("pro.system.smartApi.flowTest.col.duration")}</th>
                <th className={s.th}>{t("pro.system.smartApi.flowTest.col.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {result.steps.map((step) => (
                <tr key={step.id}>
                  <td className={s.td}>{t(`pro.system.smartApi.flowTest.step.${step.id}`)}</td>
                  <td className={s.td}>
                    <span className={step.skipped ? s.pill.neutral : step.ok ? s.pill.ok : s.pill.bad}>
                      {step.skipped
                        ? t("pro.system.smartApi.flowTest.result.skipped")
                        : step.ok
                          ? t("pro.system.smartApi.flowTest.result.ok")
                          : t("pro.system.smartApi.flowTest.result.fail")}
                    </span>
                  </td>
                  <td className={s.tdMono}>{step.status ?? "—"}</td>
                  <td className={s.tdMono}>
                    {step.skipped ? "—" : t("pro.system.smartApi.duration", { ms: step.durationMs })}
                  </td>
                  <td className={s.td}>{step.detail ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function TestUserModal({
  open,
  setOpen,
  token,
  onSignedIn,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  token: string | null;
  onSignedIn: (session: TestSession) => void;
}) {
  const { t } = useI18n();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setPassword("");
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createSmartApiTestSession(token, { email: email.trim(), password });
      if (result.smart.token && result.pro.token) {
        onSignedIn({ email: email.trim(), smartToken: result.smart.token, proToken: result.pro.token });
        setOpen(false);
      } else {
        setError(
          t("pro.system.smartApi.signIn.failed", {
            smart: result.smart.token ? "OK" : (result.smart.error ?? "—"),
            pro: result.pro.token ? "OK" : (result.pro.error ?? "—"),
          }),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pro.system.smartApi.sendFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="400px" maxWidth="480px">
      <Modal.Header>
        <Text level="h3" weight="bold">
          {t("pro.system.smartApi.signIn.title")}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <form
          id="smart-api-test-user"
          className={s.modalBody}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <Text level="body-small" color="muted">
            {t("pro.system.smartApi.signIn.body")}
          </Text>
          {error ? <Callout variant="error">{error}</Callout> : null}
          <EmailInput
            label={t("pro.system.smartApi.signIn.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            disabled={submitting}
          />
          <PasswordInput
            label={t("pro.system.smartApi.signIn.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" color="neutral" onClick={() => setOpen(false)} disabled={submitting}>
          {t("shared.common.cancel")}
        </Button>
        <Button
          type="submit"
          form="smart-api-test-user"
          color="primary"
          disabled={submitting || !email.trim() || !password}
        >
          {submitting ? t("pro.system.smartApi.signIn.submitting") : t("pro.system.smartApi.signIn.submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function SmartApiComparePage() {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("system-smart-api");

  const [catalog, setCatalog] = React.useState<SmartApiCatalog | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [endpointId, setEndpointId] = React.useState("");
  const [pathParams, setPathParams] = React.useState<Record<string, string>>({});
  const [queryText, setQueryText] = React.useState("");
  const [bodyText, setBodyText] = React.useState("");

  const [identity, setIdentity] = React.useState<Identity>("self");
  const [testSession, setTestSession] = React.useState<TestSession | null>(null);
  const [signInOpen, setSignInOpen] = React.useState(false);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<SmartApiCompareResult | null>(null);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchSmartApiCatalog(token)
      .then((data) => {
        setCatalog(data);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : t("pro.system.smartApi.loadFailed")))
      .finally(() => setLoading(false));
  }, [token, t]);

  const endpoint = catalog?.endpoints.find((e) => e.id === endpointId) ?? null;
  const grouped = React.useMemo(() => groupEndpoints(catalog?.endpoints ?? []), [catalog]);

  const chooseEndpoint = (id: string) => {
    setEndpointId(id);
    setResult(null);
    setSendError(null);
    const next = catalog?.endpoints.find((e) => e.id === id);
    setPathParams({});
    setQueryText(queryToText(next?.sampleQuery));
    setBodyText(next ? bodyToText(next) : "");
  };

  const bodyError = React.useMemo(() => {
    if (!endpoint || endpoint.method === "GET" || !bodyText.trim()) return null;
    try {
      JSON.parse(bodyText);
      return null;
    } catch {
      return t("pro.system.smartApi.bodyInvalid");
    }
  }, [endpoint, bodyText, t]);

  const missingParam = endpoint?.pathParams.some((name) => !pathParams[name]?.trim()) ?? true;
  const needsTestUser = identity === "test" && !testSession;
  const canSend = Boolean(token && endpoint) && !missingParam && !bodyError && !needsTestUser && !sending;

  const send = async (confirmWrite: boolean) => {
    if (!token || !endpoint) return;
    setSending(true);
    setSendError(null);
    try {
      const response = await compareSmartApiEndpoint(token, {
        endpointId: endpoint.id,
        pathParams,
        query: parseQueryText(queryText),
        body: endpoint.method === "GET" ? undefined : bodyText.trim() ? JSON.parse(bodyText) : {},
        auth:
          identity === "test" && testSession
            ? { smartToken: testSession.smartToken, proToken: testSession.proToken }
            : "self",
        confirmWrite,
      });
      setResult(response);
      setConfirmOpen(false);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : t("pro.system.smartApi.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (!endpoint) return;
    if (endpoint.write) {
      setConfirmOpen(true);
      return;
    }
    void send(false);
  };

  const flowTestEnabled = Boolean(catalog?.flowTestEnabled);
  const sectionLayout = bindSectionLayout([
    { id: SECTION_REQUEST, label: t("pro.system.smartApi.section.request"), icon: <SlidersHorizontal size={14} /> },
    { id: SECTION_RESULTS, label: t("pro.system.smartApi.section.results"), icon: <GitCompareArrows size={14} /> },
    { id: SECTION_DIFF, label: t("pro.system.smartApi.section.shapeDiff"), icon: <ListChecks size={14} /> },
    ...(flowTestEnabled
      ? [{ id: SECTION_FLOW, label: t("pro.system.smartApi.section.flowTest"), icon: <FlaskConical size={14} /> }]
      : []),
  ]);

  const differences = result?.shapeDiff ?? null;

  return (
    <DetailPageLayout
      header={{
        media: <GitCompareArrows size={48} />,
        title: t("pro.system.smartApi.title"),
        subtitle: catalog
          ? t("pro.system.smartApi.target", {
              smartApiUrl: catalog.smartApiUrl,
              legacyBasePath: catalog.legacyBasePath,
            })
          : t("pro.system.smartApi.subtitle"),
      }}
      {...sectionLayout}
    >
      {loadError ? <Callout variant="error">{loadError}</Callout> : null}

      <CollapsibleSection
        id={SECTION_REQUEST}
        title={t("pro.system.smartApi.section.request")}
        subtitle={t("pro.system.smartApi.subtitle")}
        icon={<SlidersHorizontal size={20} />}
        expanded={isExpanded(SECTION_REQUEST)}
        onToggleExpanded={() => toggle(SECTION_REQUEST)}
      >
        {loading && !catalog ? (
          <AuthTransitionScreen variant="compact" message={t("pro.system.smartApi.loading")} />
        ) : (
          <div className={s.sectionInner}>
            <div className={s.field}>
              <label className={s.label} htmlFor="smart-api-endpoint">
                {t("pro.system.smartApi.endpoint")}
              </label>
              <select
                id="smart-api-endpoint"
                className={s.select}
                value={endpointId}
                onChange={(e) => chooseEndpoint(e.target.value)}
              >
                <option value="">{t("pro.system.smartApi.endpointChoose")}</option>
                {grouped.map(([group, endpoints]) => (
                  <optgroup key={group} label={group}>
                    {endpoints.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.method} {e.path}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {endpoint ? (
              <>
                <div className={s.endpointSummary}>
                  <span className={s.endpointPath}>
                    {endpoint.method} {endpoint.path}
                  </span>
                  <span className={endpoint.mode === "native" ? s.pill.native : s.pill.passthrough}>
                    {t(`pro.system.smartApi.mode.${endpoint.mode}`)}
                  </span>
                  <span className={endpoint.write ? s.pill.write : s.pill.neutral}>
                    {endpoint.write ? t("pro.system.smartApi.write") : t("pro.system.smartApi.read")}
                  </span>
                  <span>{endpoint.description}</span>
                </div>
                {endpoint.mode === "passthrough" ? (
                  <p className={s.hint}>{t("pro.system.smartApi.passthroughHint")}</p>
                ) : null}

                {endpoint.pathParams.length > 0 ? (
                  <div className={s.field}>
                    <span className={s.label}>{t("pro.system.smartApi.pathParams")}</span>
                    <div className={s.formGrid}>
                      {endpoint.pathParams.map((name) => (
                        <TextInput
                          key={name}
                          label={`:${name}`}
                          value={pathParams[name] ?? ""}
                          onChange={(e) => setPathParams((prev) => ({ ...prev, [name]: e.target.value }))}
                          disabled={sending}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className={s.formGrid}>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="smart-api-query">
                      {t("pro.system.smartApi.query")}
                    </label>
                    <textarea
                      id="smart-api-query"
                      className={s.textarea}
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                      spellCheck={false}
                      disabled={sending}
                    />
                    <p className={s.hint}>{t("pro.system.smartApi.queryHint")}</p>
                  </div>
                  {endpoint.method !== "GET" ? (
                    <div className={s.field}>
                      <label className={s.label} htmlFor="smart-api-body">
                        {t("pro.system.smartApi.body")}
                      </label>
                      <textarea
                        id="smart-api-body"
                        className={s.textarea}
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        spellCheck={false}
                        disabled={sending}
                      />
                      {bodyError ? <Callout variant="error">{bodyError}</Callout> : null}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            <div className={s.field}>
              <span className={s.label}>{t("pro.system.smartApi.identity")}</span>
              <div className={s.authRow}>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="smart-api-identity"
                    checked={identity === "self"}
                    onChange={() => setIdentity("self")}
                  />
                  {t("pro.system.smartApi.identity.self")}
                </label>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="smart-api-identity"
                    checked={identity === "test"}
                    onChange={() => setIdentity("test")}
                  />
                  {t("pro.system.smartApi.identity.testUser")}
                </label>
              </div>
              {identity === "self" ? (
                <p className={s.hint}>{t("pro.system.smartApi.identity.selfHint")}</p>
              ) : (
                <div className={s.sessionStatus}>
                  <span>
                    {testSession
                      ? t("pro.system.smartApi.identity.signedInAs", { email: testSession.email })
                      : t("pro.system.smartApi.identity.notSignedIn")}
                  </span>
                  <Button size="sm" variant="outline" color="neutral" onClick={() => setSignInOpen(true)}>
                    {testSession ? t("pro.system.smartApi.identity.switch") : t("pro.system.smartApi.identity.signIn")}
                  </Button>
                  {testSession ? (
                    <Button size="sm" variant="ghost" color="neutral" onClick={() => setTestSession(null)}>
                      {t("pro.system.smartApi.identity.signOut")}
                    </Button>
                  ) : null}
                </div>
              )}
            </div>

            {sendError ? <Callout variant="error">{sendError}</Callout> : null}

            <div className={s.actionsRow}>
              <Button color="primary" onClick={handleSend} disabled={!canSend}>
                <Send size={14} />
                {sending ? t("pro.system.smartApi.sending") : t("pro.system.smartApi.send")}
              </Button>
            </div>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_RESULTS}
        title={t("pro.system.smartApi.section.results")}
        subtitle={result ? `${result.request.method} ${result.request.path}` : undefined}
        icon={<GitCompareArrows size={20} />}
        expanded={isExpanded(SECTION_RESULTS)}
        onToggleExpanded={() => toggle(SECTION_RESULTS)}
      >
        {result ? (
          <div className={s.sectionInner}>
            <div className={s.diffSummary}>
              <span className={result.statusMatch ? s.pill.ok : s.pill.bad}>
                {result.statusMatch ? t("pro.system.smartApi.statusMatch") : t("pro.system.smartApi.statusMismatch")}
              </span>
              {differences ? (
                <span className={differences.length === 0 ? s.pill.ok : s.pill.bad}>
                  {differences.length === 0
                    ? t("pro.system.smartApi.shapeMatch")
                    : t("pro.system.smartApi.shapeDifferences", { count: differences.length })}
                </span>
              ) : null}
            </div>
            <div className={s.columns}>
              <SideColumn title={t("pro.system.smartApi.smartSide")} side={result.smart} />
              <SideColumn title={t("pro.system.smartApi.proSide")} side={result.pro} />
            </div>
          </div>
        ) : (
          <Text level="body-small" color="muted">
            {t("pro.system.smartApi.noResults")}
          </Text>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_DIFF}
        title={t("pro.system.smartApi.section.shapeDiff")}
        icon={<ListChecks size={20} />}
        expanded={isExpanded(SECTION_DIFF)}
        onToggleExpanded={() => toggle(SECTION_DIFF)}
      >
        {!result ? (
          <Text level="body-small" color="muted">
            {t("pro.system.smartApi.noResults")}
          </Text>
        ) : differences === null ? (
          <Text level="body-small" color="muted">
            {t("pro.system.smartApi.shapeUnavailable")}
          </Text>
        ) : differences.length === 0 ? (
          <Text level="body-small" color="muted">
            {t("pro.system.smartApi.shapeMatch")}
          </Text>
        ) : (
          <ShapeDiffTable differences={differences} />
        )}
      </CollapsibleSection>

      {flowTestEnabled ? (
        <CollapsibleSection
          id={SECTION_FLOW}
          title={t("pro.system.smartApi.section.flowTest")}
          icon={<FlaskConical size={20} />}
          expanded={isExpanded(SECTION_FLOW)}
          onToggleExpanded={() => toggle(SECTION_FLOW)}
        >
          <FlowTestPanel token={token} />
        </CollapsibleSection>
      ) : null}

      <TestUserModal
        open={signInOpen}
        setOpen={setSignInOpen}
        token={token}
        onSignedIn={(session) => {
          setTestSession(session);
          setIdentity("test");
        }}
      />

      <ConfirmModal
        open={confirmOpen}
        title={t("pro.system.smartApi.confirmWrite.title")}
        body={
          endpoint
            ? t("pro.system.smartApi.confirmWrite.body", { method: endpoint.method, path: endpoint.path })
            : ""
        }
        confirmLabel={t("pro.system.smartApi.confirmWrite.confirm")}
        confirmColor="warning"
        loading={sending}
        error={sendError}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void send(true)}
      />
    </DetailPageLayout>
  );
}
